import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { 
  PlayIcon, 
  CheckCircleIcon, 
  QuestionMarkCircleIcon,
  HandRaisedIcon,
  CodeBracketIcon,
  BellIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { NodeType, ReleaseFlowNodePipelineLinkDto } from '../../types/releaseFlow';
import PipelineSelectionModal from './PipelineSelectionModal';

interface CustomNodeData {
  label: string;
  nodeType: NodeType;
  pipelineId?: string;
  pipelineName?: string;
  configuration?: string;
  description?: string;
  isActive: boolean;
  backgroundColor?: string;
  borderColor?: string;
  width?: string;
  height?: string;
  pipelineAssociations?: ReleaseFlowNodePipelineLinkDto[];
  // Additional context data
  releaseFlowId?: string;
  readOnly?: boolean;
}

interface CustomNodeProps {
  id: string;
  data: CustomNodeData;
  selected: boolean;
}

const getNodeIcon = (nodeType: NodeType) => {
  const iconProps = { className: "w-5 h-5" };
  
  switch (nodeType) {
    case 'pipeline':
      return <PlayIcon {...iconProps} />;
    case 'approval':
      return <CheckCircleIcon {...iconProps} />;
    case 'condition':
      return <QuestionMarkCircleIcon {...iconProps} />;
    case 'manual':
      return <HandRaisedIcon {...iconProps} />;
    case 'script':
      return <CodeBracketIcon {...iconProps} />;
    case 'notification':
      return <BellIcon {...iconProps} />;
    default:
      return <PlayIcon {...iconProps} />;
  }
};

const getNodeColors = (nodeType: NodeType, backgroundColor?: string, borderColor?: string) => {
  if (backgroundColor && borderColor) {
    return { backgroundColor, borderColor };
  }
  
  switch (nodeType) {
    case 'pipeline':
      return { backgroundColor: '#dbeafe', borderColor: '#3b82f6' };
    case 'approval':
      return { backgroundColor: '#dcfce7', borderColor: '#22c55e' };
    case 'condition':
      return { backgroundColor: '#fef3c7', borderColor: '#f59e0b' };
    case 'manual':
      return { backgroundColor: '#fde2e8', borderColor: '#ef4444' };
    case 'script':
      return { backgroundColor: '#e0e7ff', borderColor: '#6366f1' };
    case 'notification':
      return { backgroundColor: '#f3e8ff', borderColor: '#a855f7' };
    default:
      return { backgroundColor: '#f3f4f6', borderColor: '#6b7280' };
  }
};

export const CustomNode: React.FC<CustomNodeProps> = ({ 
  id, 
  data, 
  selected 
}) => {
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  
  const { backgroundColor, borderColor } = getNodeColors(
    data.nodeType,
    data.backgroundColor,
    data.borderColor
  );

  const pipelineAssociations = data.pipelineAssociations || [];
  const hasPipelines = pipelineAssociations.length > 0;
  const releaseFlowId = data.releaseFlowId;
  const readOnly = data.readOnly || false;

  // Get the primary pipeline association for display
  const primaryPipeline = pipelineAssociations.length > 0 ? pipelineAssociations[0] : null;
  
  // Determine display name: use pipeline name if available, otherwise node name
  const displayName = data.nodeType === 'pipeline' && primaryPipeline 
    ? primaryPipeline.pipelineName 
    : data.label;

  // For pipeline nodes: show "Select Pipeline" if no pipeline, otherwise show pipeline name
  const displayText = data.nodeType === 'pipeline' 
    ? (hasPipelines ? displayName : 'Select Pipeline')
    : displayName;

  const handleNodeClick = (e: React.MouseEvent) => {
    if (data.nodeType === 'pipeline' && !readOnly && releaseFlowId) {
      e.stopPropagation();
      setShowPipelineModal(true);
    }
  };

  const handleModalClose = () => {
    setShowPipelineModal(false);
  };

  // Determine if node needs pipeline association
  const needsPipeline = data.nodeType === 'pipeline' && !hasPipelines;
  const hasPipelineDeleted = primaryPipeline?.isDeleted === true;

  // Visual style: deleted pipeline takes priority over needs-pipeline
  const nodeBackgroundColor = hasPipelineDeleted ? '#fffbeb' : needsPipeline ? '#fef2f2' : backgroundColor;
  const nodeBorderColor = hasPipelineDeleted ? '#f59e0b' : needsPipeline ? '#f87171' : borderColor;

  return (
    <>
      <div
        className={`px-4 py-3 shadow-lg rounded-lg border-2 transition-all duration-200 ${
          selected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
        } ${!data.isActive ? 'opacity-60' : ''} ${hasPipelineDeleted ? 'opacity-75' : ''} ${
          data.nodeType === 'pipeline' && !readOnly && releaseFlowId ? 'cursor-pointer hover:shadow-xl' : ''
        }`}
        style={{
          backgroundColor: nodeBackgroundColor,
          borderColor: nodeBorderColor,
          borderStyle: hasPipelineDeleted ? 'dashed' : 'solid',
          minWidth: parseInt(data.width || '220', 10),
          minHeight: parseInt(data.height || '100', 10),
        }}
        onClick={handleNodeClick}
      >
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-gray-400 !border-gray-600"
        />
        
        <div className="flex items-start justify-between">
          {/* Node Content */}
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {getNodeIcon(data.nodeType)}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold truncate ${
                hasPipelineDeleted ? 'text-amber-700' : needsPipeline ? 'text-red-700' : 'text-gray-900'
              }`}>
                {displayText}
              </div>
              
              {hasPipelineDeleted && (
                <div className="flex items-center mt-1 text-xs text-amber-600" title="Pipeline removed from Azure DevOps">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">Pipeline removed from Azure DevOps</span>
                </div>
              )}

              {data.description && (
                <div className="text-xs text-gray-500 truncate mt-1">
                  {data.description}
                </div>
              )}
            </div>
          </div>
        </div>

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 !bg-gray-400 !border-gray-600"
        />
      </div>

      {/* Pipeline Selection Modal */}
      {releaseFlowId && (
        <PipelineSelectionModal
          isOpen={showPipelineModal}
          onClose={handleModalClose}
          releaseFlowId={releaseFlowId}
          nodeId={id}
          nodeName={data.label}
          existingPipeline={primaryPipeline ? {
            pipelineDefinitionId: primaryPipeline.pipelineDefinitionId,
            pipelineName: primaryPipeline.pipelineName
          } : undefined}
          onPipelineChange={() => {
            // This will trigger a refetch of the release flow data
            // which will update the node's pipeline associations
          }}
        />
      )}
    </>
  );
};

export const nodeTypes = {
  custom: CustomNode,
};